import type React from "react";
import { Navigate, useParams } from "react-router";
import { CenterSpinner } from "../atoms/spinner";
import { useDocumentContent } from "../api/v2/useDocumentContent";
import { useEffect, useRef, useState } from "react";
import { type DocumentEditorContainerComponent } from "@syncfusion/ej2-react-documenteditor";
import { DocumentVersionStatus } from "../config/types";
import Editor from "../organisms/Editor";
import { useLanguageContext } from "../context/language";
import { useUserContext } from "../hooks/useUserContext";
import { type DocumentVersionDetails } from "../api/types";
import { printFullName } from "../helpers/user";
import { setPageName } from "../helpers/editor";
import { useDocumentVersion } from "../api/v2/useDocumentVersion";
import { isWopiTestFileType } from "../helpers/document";
import { useTranslation } from "react-i18next";
import { Cloud, CloudUpload } from "lucide-react";
import { lockDocument, unlockDocument } from "../api/v2/requests";

const DocumentEditorPage = () => {
  const { id } = useParams();
  const { data, isLoading } = useDocumentVersion(id);
  const { t } = useTranslation();
  const { user } = useUserContext();

  const [lockLoading, setLockLoading] = useState(false);
  const [lockState, setLockState] = useState<"mine" | "taken" | undefined>();

  useEffect(() => {
    const lockRequest = async () => {
      try {
        setLockLoading(true);
        const lockResponse = await lockDocument(Number(id));
        setLockState(lockResponse.userId === user.id ? "mine" : "taken");
      } catch {
        setLockState("taken");
      } finally {
        setLockLoading(false);
      }
    };

    if (lockState === undefined && lockLoading === false) {
      void lockRequest();
    }
  }, [data, id, lockLoading, lockState, user]);

  if (isLoading || lockLoading) {
    return <CenterSpinner withText />;
  }

  if (!data || !lockState) {
    return <div>{t(($) => $.commons.errors.noDocument)}</div>;
  }

  return <PageContent data={data} isActiveUser={lockState === "mine"} />;
};

interface Props {
  data: DocumentVersionDetails;
  isActiveUser: boolean;
}

const PageContent: React.FC<Props> = ({ data, isActiveUser }) => {
  const containerRef = useRef<DocumentEditorContainerComponent | null>(null);
  const { user } = useUserContext();
  const { language } = useLanguageContext();
  const { t } = useTranslation();

  const { data: documentContent, isLoading } = useDocumentContent(data.id);

  const [hasUnsavedChanges, sethasUnsavedChanges] = useState(false);

  const isDraft = data.status === DocumentVersionStatus.draft;

  setPageName(data.title);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
      }
      if (isActiveUser) {
        void unlockDocument(data.id);
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [data.id, hasUnsavedChanges, isActiveUser]);

  if (isWopiTestFileType(data.fileType)) {
    return <Navigate to={`/versions/${data.id}/editor`} replace />;
  }

  if (data.fileType !== "sfdt") {
    return <Navigate to={`/documents/${data.id}`} replace />;
  }

  return (
    <>
      <div className="relative h-[calc(100vh-16px)] overflow-y-hidden">
        {isLoading ? (
          <CenterSpinner withText />
        ) : (
          <Editor
            containerRef={containerRef}
            documentId={data.id}
            documentContent={documentContent}
            documentStatus={data.status}
            locale={language}
            fullName={printFullName(user)}
            isActiveUser={isActiveUser}
            isReadOnlyStatus={!isDraft}
            setHasUnsavedChanges={sethasUnsavedChanges}
          />
        )}
      </div>
      <div className="text-muted-foreground flex justify-center gap-1">
        {hasUnsavedChanges ? (
          <>
            <CloudUpload className="size-4" />
            <span className="text-xs">
              {t(($) => $.commons.buttons.saving)}
            </span>
          </>
        ) : (
          <>
            <Cloud className="size-4" />
            <span className="text-xs">{t(($) => $.commons.buttons.saved)}</span>
          </>
        )}
      </div>
    </>
  );
};

export default DocumentEditorPage;
